// =================================================================================================
// Drop-down field for selecting an option from a list
// =================================================================================================

import { Select, MenuItem, FormControl, InputLabel, ListSubheader, Alert } from '@mui/material';
import { useContext, useEffect, useState } from 'react';
import { getFieldOptions, replacePlaceholders } from '../../formHandling';
import { JourneyContext } from '../../JourneyContext';
import { TFieldChangeHandler, TFormField } from '../../types';

// =================================================================================================
// Main component
// =================================================================================================

export const SelectField = (props: { field: TFormField; value: any; changeHandler: TFieldChangeHandler }) => {
  const { field, value, changeHandler } = props;
  const journeyContext = useContext(JourneyContext);
  // Options can either be an array of strings, or a { label: string, value: any } object
  const groups = field?.params?.groups;
  const localOptions = getFieldOptions(field, journeyContext.formData);

  const [declineMessage, setDeclineMessage] = useState<string | null>(null);

  const groupedItems = groupItems(localOptions, groups);


  const localChangeHandler = (event: any) => {
    if (field.triggerDecline && localOptions) {
      const selected = localOptions.find((o) => o.value === event.target.value);
      console.log('selected', selected)
      if (selected?.declineMessage) {
        setDeclineMessage(selected.declineMessage);
        changeHandler(field, { value: event.target.value, isDeclined: true });
        changeHandler(field, event.target.value);
        return;
      } else {
        setDeclineMessage(null);
      }
    }
    changeHandler(field, event.target.value);
  };

  // -----------------------------------------------------------------------------------------------
  // Main render
  // -----------------------------------------------------------------------------------------------

  useEffect(() => {
    const selected = localOptions.find((opt) => opt.value === value);
    if (selected?.disabled) {
      changeHandler(field, undefined);
    }
  });

  return (
    <>
      <FormControl fullWidth required={field.required}>
        <InputLabel id={field.label + '-label'}>{field.title}</InputLabel>
        <Select
          disabled={field.disabled}
          labelId={field.label + '-label'}
          label={field.title}
          value={value ?? ''}
          onChange={localChangeHandler}
          MenuProps={{
            style: {
              maxHeight: 400,
            },
          }}
        >
          {groups
            ? groupedItems?.map((group, index) => (
              <>
                <ListSubheader>{group.label}</ListSubheader>
                {group?.options?.map((option, index) => (
                  <MenuItem
                    sx={{ whiteSpace: 'pre-wrap', maxWidth: '600px' }}
                    key={index}
                    value={option.value}
                    disabled={option.disabled}
                  >
                    {option.label}
                  </MenuItem>
                ))}
              </>
            ))
            : localOptions.map((option, index) => {
              return (
                <MenuItem
                  sx={{ whiteSpace: 'pre-wrap', maxWidth: '600px' }}
                  key={index}
                  value={option.value}
                  disabled={option.disabled}
                >
                  {option.label}
                </MenuItem>
              );
            })}

        </Select>
      </FormControl>
      {declineMessage && (
        <Alert
          sx={{ fontWeight: '600', mt: 2 }}
          variant="standard"
          severity="error"
        >
          {declineMessage}
        </Alert>
      )}
    </>
  );
};

interface GroupedItems {
  label: string;
  options: ReturnType<typeof getFieldOptions>;
}

export function groupItems(
  options: ReturnType<typeof getFieldOptions>,
  groups?: Record<string, string[]>,
): GroupedItems[] | undefined {
  if (!groups || !options) return;
  return Object.entries(groups).map(([groupName, values]) => ({
    label: groupName,
    options: options?.filter((item) => values.includes(item.value)),
  }));
}
